import i18nFactory from '@yandex-int/i18n';
import { cloneDeep } from 'lodash';

import * as keyset from './Experiments.i18n';

const i18n = i18nFactory(keyset);

export interface ServerRate {
    ab_segment_multiplier_value_id: number;
    section_id: number;
    segment_id: number;
    multiplier_pct: number;
}

export interface ServerSegment {
    segment_id: number;
    segment_name: string;
    percent: number;
    archive?: number;
}

export interface ServerSection {
    section_id: number;
    section_name: string;
    segments: Array<ServerSegment>;
    archive?: number;
}

export interface TreeSegment {
    id: number;
}

export interface TreeSection {
    id: number;
    segments: Array<TreeSegment>;
}

export interface Segment extends ServerSegment {
    disabled: boolean;
    selected: boolean;
    rate?: Rate;
}

export interface Section {
    section_id: number;
    section_name: string;
    disabled: boolean;
    selected: boolean;
    segments: Record<string, Segment>;
    tooltip?: string;
    archive?: number;
}

export interface Rate {
    id: number;
    value: number | undefined;
}

/**
 * Совпадение строки с поисковой строкой
 * @param {string} source
 * @param {string} search
 * @return {boolean}
 */
const isSearchMatched = (source: string, search: string): boolean => {
    return source.toLowerCase().indexOf(search.toLowerCase()) !== -1;
};

/**
 * Вычисление отображаемых экспериментов
 */
export const calcSectionsTree = (
    treeData: Record<string, Section>,
    treeOrder: Array<TreeSection>,
    showOnlySelected: boolean
): Array<TreeSection> => {
    if (!showOnlySelected) {
        return treeOrder;
    } else {
        return treeOrder.filter(section => treeData[section.id].selected);
    }
};

/**
 * Включает/отключает возможность выбора секций
 */
export const calcDisabledSections = (treeData: Record<string, Section>): Record<string, Section> => {
    const isLimitReached = isSectionLimitReached(treeData);

    return Object.keys(treeData).reduce((result, sectionId) => {
        return {
            ...result,
            [sectionId]: {
                ...treeData[sectionId],
                disabled: isSectionDisabled(treeData[sectionId]) || (isLimitReached && !treeData[sectionId].selected)
            }
        };
    }, {});
};

/**
 * Вычисление отображаемых сегментов
 */
export const calcSegmentsTree = (
    treeData: Record<string, Section>,
    treeOrder: Array<TreeSection>,
    showOnlySelected: boolean
): Array<TreeSection> => {
    const newTree: Array<TreeSection> = [];

    treeOrder.forEach(sect => {
        const section = treeData[sect.id];

        if (section.selected) {
            if (showOnlySelected) {
                const newSect = {
                    ...sect,
                    segments: sect.segments.filter(seg => section.segments[seg.id].selected)
                };
                if (newSect.segments.length) {
                    newTree.push(newSect);
                }
            } else {
                newTree.push(sect);
            }
        }
    });

    return newTree;
};

/**
 * Вычисление отображаемых сегментов в корректировках
 */
export const calcSegmentsRatesTree = (
    treeData: Record<string, Section>,
    treeOrder: Array<TreeSection>,
    showOnlySelected: boolean,
    isTargetingActive: boolean
): Array<TreeSection> => {
    const newTree: Array<TreeSection> = [];

    treeOrder.forEach(sect => {
        const section = treeData[sect.id];
        if (section.selected) {
            const availableSect = {
                ...sect,
                segments: sect.segments.filter(seg => section.segments[seg.id].selected)
            };

            if (!availableSect.segments.length || !isTargetingActive) {
                availableSect.segments = sect.segments;
            }

            if (showOnlySelected) {
                const newSect = {
                    ...availableSect,
                    segments: availableSect.segments.filter(
                        seg => section.segments[seg.id].rate && section.segments[seg.id].rate!.value
                    )
                };
                if (newSect.segments.length) {
                    newTree.push(newSect);
                }
            } else {
                newTree.push(availableSect);
            }
        }
    });

    return newTree;
};

/**
 * Фильтрация экспериментов с учетом поиска
 */
export const filterSectionsBySearch = (
    treeData: Record<string, Section>,
    treeOrder: Array<TreeSection>,
    search: string
): Array<TreeSection> => {
    if (!search.length) {
        return treeOrder;
    } else {
        return treeOrder.filter(section => isSearchMatched(treeData[section.id].section_name, search));
    }
};

/**
 * Фильтрация сегментов с учетом поиска
 */
export const filterSegmentsBySearch = (
    treeData: Record<string, Section>,
    treeOrder: Array<TreeSection>,
    search: string
): Array<TreeSection> => {
    const newTree: Array<TreeSection> = [];

    treeOrder.forEach(sect => {
        const section = treeData[sect.id];

        if (isSearchMatched(section.section_name, search)) {
            newTree.push(sect);
        } else {
            const newSect = {
                ...sect,
                segments: sect.segments.filter(seg => isSearchMatched(section.segments[seg.id].segment_name, search))
            };
            if (newSect.segments.length) {
                newTree.push(newSect);
            }
        }
    });

    return newTree;
};

/**
 * Преобразование входного дерева, в удобное для использования
 */
export const getTreeData = (
    sections: Array<ServerSection>,
    checkedSections: Array<ServerSection['section_id']>,
    checkedSegments: Array<ServerSegment['segment_id']>,
    rates: Array<ServerRate>
): Record<string, Section> => {
    const ratesHash = rates.reduce((hash, data) => {
        hash[data.segment_id] = data;
        return hash;
    }, {});

    const dataTree = sections.reduce((hash, section: ServerSection): Record<string, Section> => {
        const segments = section.segments.reduce((segmentsHash, segment: ServerSegment): Record<string, Segment> => {
            const isSegSelected = checkedSegments.includes(segment.segment_id);
            const rate = ratesHash[segment.segment_id]
                ? {
                      id: ratesHash[segment.segment_id].ab_segment_multiplier_value_id,
                      value: ratesHash[segment.segment_id].multiplier_pct - 100
                  }
                : undefined;

            segmentsHash[segment.segment_id] = {
                ...segment,
                selected: isSegSelected,
                rate: rate
            };
            return segmentsHash;
        }, {});
        const isSelected: boolean = checkedSections.includes(section.section_id);

        const isDisabled: boolean = isSectionDisabled({
            ...section,
            selected: isSelected,
            segments,
            disabled: false
        });

        hash[String(section.section_id)] = {
            ...section,
            selected: isSelected,
            disabled: isDisabled,
            segments: {
                ...calcDisabledSegments(segments)
            }
        };

        return hash;
    }, {});

    return calcDisabledSections(dataTree);
};

/**
 * Преобразования входного дерева, в дерево для хранения порядка
 */
export const getOrderTree = (sections: Array<ServerSection>): Array<TreeSection> => {
    return sections.map(s => ({
        id: s.section_id,
        segments: s.segments.map(seg => ({
            id: seg.segment_id
        }))
    }));
};

/**
 * Вычисление хеша сегмент->эксперимент
 * @param {Array<ServerSection>} sections
 * @return {Record<string, number>}
 */
export const getSegmentInverseIndex = (sections: Array<ServerSection>): Record<string, number> => {
    return sections.reduce((hash, section: ServerSection): Record<string, number> => {
        return section.segments.reduce((segmentsHash, segment: ServerSegment): Record<string, number> => {
            hash[segment.segment_id] = section.section_id;
            return hash;
        }, hash);
    }, {});
};

/**
 * Проверяет нужно ли выключить переданный экперимент
 */
const isSectionDisabled = (section: Section): boolean => {
    return !!section.archive && !section.selected;
};

/**
 * Проверяет нужно ли выключить сегменты в эксперименте
 */
const calcDisabledSegments = (segments: Record<string, Segment>): Record<string, Segment> => {
    const selectedIds: Array<string> = [];

    Object.keys(segments).forEach(segmentId => {
        const segment = segments[segmentId];

        if (segment.selected) {
            selectedIds.push(segmentId);
        }

        segments = {
            ...segments,
            [segmentId]: {
                ...segment,
                disabled: !!segment.archive && !segment.selected && !segment.disabled
            }
        };
    });

    if (selectedIds.length === 1) {
        segments = {
            ...segments,
            [selectedIds[0]]: {
                ...segments[selectedIds[0]],
                disabled: true
            }
        };
    }

    return {
        ...segments
    };
};

/**
 * Проверяет достигнут ли лимит выбранных секций
 */
const isSectionLimitReached = (treeData: Record<string, Section>): boolean => {
    return (
        Object.keys(treeData).reduce((count, sectionId) => {
            if (treeData[sectionId].selected) {
                count++;
            }
            return count;
        }, 0) >= 5
    );
};

/**
 * Переключение состояния эксперимента
 */
export const toggleSection = (
    treeData: Record<string, Section>,
    id: Section['section_id']
): Record<string, Section> => {
    const section = treeData[id];
    const newTreeData = {
        ...treeData,
        [id]: {
            ...section,
            selected: !section.selected
        }
    };

    return calcDisabledSections(newTreeData);
};

/**
 * Переключение состояния сегмента
 */
export const toggleSegment = (
    treeData: Record<string, Section>,
    id: Segment['segment_id'],
    sectionId: Section['section_id']
): Record<string, Section> => {
    let section: Section = treeData[sectionId];
    let segment = section.segments[id];

    segment = {
        ...segment,
        selected: !segment.selected
    };

    section = {
        ...section,
        segments: {
            ...section.segments,
            [String(id)]: segment
        }
    };

    const isSectDisabled: boolean = isSectionDisabled(section);

    return {
        ...treeData,
        [sectionId]: {
            ...section,
            disabled: isSectDisabled,
            segments: calcDisabledSegments(section.segments)
        }
    };
};

/**
 * Переключение использования таргетинга
 */
export const toggleTargetingStatus = (
    treeData: Record<string, Section>,
    isTargetingEmpty: boolean,
    newStatus: boolean
): Record<string, Section> => {
    const newTreeData = cloneDeep(treeData);

    if (newStatus) {
        Object.keys(newTreeData).forEach(sectionId => {
            const section = newTreeData[sectionId];

            Object.keys(section.segments).forEach(segmentId => {
                if (isTargetingEmpty) {
                    section.segments[segmentId].selected = true;
                } else {
                    if (!section.selected) {
                        section.segments[segmentId].selected = false;
                    }
                }
            });
        });
    }

    return newTreeData;
};

export const changeRate = (
    treeData: Record<string, Section>,
    sectionId: Section['section_id'],
    segmentId: Segment['segment_id'],
    rate: Rate
): Record<string, Section> => {
    return {
        ...treeData,
        [sectionId]: {
            ...treeData[sectionId],
            segments: {
                ...treeData[sectionId].segments,
                [segmentId]: {
                    ...treeData[sectionId].segments[segmentId],
                    rate: rate
                }
            }
        }
    };
};

export const formatName = (name: string, archive: boolean): string => {
    return archive
        ? i18n('experimentsArchive', {
              experimentsArchiveName: name
          })
        : name;
};
